/** Pure helpers shared by the search filter bar effects. */
import { MultiselectProps } from '@amzn/awsui-components-console';
import type { SearchFilters } from 'src/api/graphql/searchTypes';
import type { StringGroupItem } from './searchFilterData';

/** The setter signature `SearchFilterBar` receives from its parent. */
export type FiltersUpdater = (
  update: SearchFilters | ((prev: SearchFilters) => SearchFilters),
) => void;

/**
 * Filter keys holding a list of ids that is derived from a parent selection and
 * therefore has to be pruned when that parent selection changes.
 */
export type DependentFilterKey = 'stringGroupIds' | 'cxIds';

/** String groups fetched for one namespace, plus whether the fetch was capped. */
export interface CachedStringGroups {
  readonly groups: readonly StringGroupItem[];
  readonly truncated: boolean;
}

/**
 * Drops selected ids under `filterKey` that are no longer offered by
 * `validOptions`. Shared by the String Group and CXID paths, which have
 * identical semantics:
 * - Widening the parent selection preserves valid selections
 * - Narrowing it drops only the now-invalid ids
 * - Dropping every id clears the key (undefined) rather than leaving `[]`
 * - No-op when there is nothing to drop
 *
 * The read + check + compute happens entirely inside the functional updater so
 * that a fetch which resolves after the user changed their selection prunes
 * against the latest state rather than a stale render-time closure.
 */
export function pruneInvalidSelections(
  onFiltersChange: FiltersUpdater,
  filterKey: DependentFilterKey,
  validOptions: readonly MultiselectProps.Option[],
): void {
  onFiltersChange((prev) => {
    const selected = prev[filterKey];
    if (!selected?.length) return prev;
    const validValues = new Set(validOptions.map((opt) => opt.value));
    const pruned = selected.filter((id) => validValues.has(id));
    return pruned.length < selected.length
      ? { ...prev, [filterKey]: pruned.length ? pruned : undefined }
      : prev;
  });
}

/**
 * Builds the union of string group options across the selected namespaces.
 * Dedupes by stringGroupId and stable-sorts alphabetically.
 */
export function buildStringGroupUnion(
  namespaces: readonly string[],
  cache: ReadonlyMap<string, CachedStringGroups>,
): MultiselectProps.Option[] {
  const seen = new Set<string>();
  const allGroups: StringGroupItem[] = [];

  for (const ns of namespaces) {
    const groups = cache.get(ns)?.groups || [];
    for (const g of groups) {
      if (!seen.has(g.stringGroupId)) {
        seen.add(g.stringGroupId);
        allGroups.push(g);
      }
    }
  }

  return allGroups
    .sort((a, b) => a.stringGroupId.localeCompare(b.stringGroupId))
    .map((g) => ({ label: g.stringGroupId, value: g.stringGroupId }));
}

/**
 * True when any of the selected namespaces had its string group fetch capped —
 * drives the truncation hint on the String Group dropdown.
 */
export function hasTruncatedStringGroups(
  namespaces: readonly string[],
  cache: ReadonlyMap<string, CachedStringGroups>,
): boolean {
  return namespaces.some((ns) => cache.get(ns)?.truncated === true);
}

/** Stable "nothing selected" filters object used when clearing. */
export const NO_FILTERS: SearchFilters = {};

/**
 * True when the user has narrowed the search in any way — every dropdown
 * filter, plus the exact-match toggle and the free-text query, all of which the
 * Clear filters action resets.
 *
 * Takes the individual pieces rather than the whole `SearchState` so this module
 * stays free of a dependency on the page component.
 *
 * Do NOT reuse this to gate search submission — see
 * `hasMinimumSearchCriteria` below for why the two must stay separate.
 */
export function hasActiveFilters(
  filters: SearchFilters,
  queryText: string,
  exactMatch: boolean,
): boolean {
  if (queryText.trim().length > 0) return true;
  if (exactMatch) return true;
  return Object.values(filters).some(
    (value) => Array.isArray(value) && value.length > 0,
  );
}

/**
 * Filter keys that actually NARROW a source-string search, and therefore count
 * as a search criterion.
 *
 * `translatedLocales` is deliberately absent: it exists only in translation
 * mode, which has its own required-filter validation (see
 * `SearchResultsTable.doSearch`). Listing it here would quietly extend the
 * minimum-criteria rule to translation search.
 */
const CRITERION_FILTER_KEYS = [
  'namespaceIds',
  'statuses',
  'stringGroupIds',
  'cxIds',
  'cxFamilies',
] as const satisfies readonly (keyof SearchFilters)[];

/**
 * True when the user has supplied at least one thing to search ON: a non-blank
 * free-text query, or at least one populated narrowing filter.
 *
 * WHY THIS IS NOT `hasActiveFilters`
 * ----------------------------------
 * The two predicates look similar and MUST NOT be deduplicated — they answer
 * different questions and differ on `exactMatch`:
 *
 * - `hasActiveFilters` backs the "Clear filters" button, i.e. "is there
 *   anything for Clear to reset?". `exactMatch: true` IS resettable state, so it
 *   counts there.
 * - `hasMinimumSearchCriteria` backs search submission, i.e. "is the result set
 *   bounded?". `exactMatch` only changes HOW `queryText` is matched — with no
 *   query text and no filters it narrows nothing at all, so treating it as a
 *   criterion would let a completely unbounded query through. It is therefore
 *   not even accepted as a parameter here.
 *
 * NOTE — this is a UI-layer product decision, not a backend limitation.
 * `SearchSourceStringsRequest.queryText` in the Smithy model is explicitly
 * documented "Omit or leave empty for browse mode (match_all)", so the API
 * still supports an empty search on purpose. LangHub restricts it in the UI to
 * avoid unbounded queries; nothing about the backend capability changed.
 */
export function hasMinimumSearchCriteria(
  filters: SearchFilters,
  queryText: string,
): boolean {
  if (queryText.trim().length > 0) return true;
  return CRITERION_FILTER_KEYS.some((key) => (filters[key]?.length ?? 0) > 0);
}
